import { createElement as rc } from 'react';
import PropTypes from 'prop-types';
import { NavTab as _NavTab, styled, View, webOnlyStyles, fromTheme } from 'lib_ui-primitives';
import useHiddenForViewPort from '../../hooks/useHiddenForViewPort';
import useIsInActiveRoute from '../../hooks/useIsInActiveRoute';
import useDisabledForNetwork from '../../hooks/useDisabledForNetwork';
import { useTabHover, useTabFocus } from '../layout/TabViewLayout/Tab.gl2025.shared';

// Outer wrapper carries all GL2025 visual styling via mode array
// This avoids leaking mode/styling props through NavTab's ...rest spread
let TabWrapper = styled(View).attrs({ name: 'tab-nav-heading-gl2025', role: 'tab' })`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    user-select: none;

    /* Reset borders and remove default browser focus outline */
    border: none;
    outline: none;

    /* Dynamic styles based on state */
    height: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'container', 'height')({ theme })};
    padding-top: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'container', 'paddingTop')({ theme })};
    padding-bottom: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'container', 'paddingBottom')({ theme })};
    padding-left: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'container', 'paddingLeft')({ theme })};
    padding-right: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'container', 'paddingRight')({ theme })};

    /* Bottom border for current tabs when not focused */
    ${({ mode, theme }) =>
        mode.includes('current') && !mode.includes('focus')
            ? `
                border-bottom-width: ${fromTheme('tabs', ...mode, 'container', 'borderBottomWidth')({ theme })};
                border-bottom-color: ${fromTheme('tabs', ...mode, 'container', 'borderBottomColor')({ theme })};
                border-bottom-style: solid;
            `
            : ''
    }

    /* Full border with radius for focus state */
    ${({ mode, theme }) =>
        mode.includes('focus')
            ? `
                border-width: ${fromTheme('tabs', ...mode, 'container', 'borderWidth')({ theme })};
                border-color: ${fromTheme('tabs', ...mode, 'container', 'borderColor')({ theme })};
                border-style: solid;
                border-radius: ${fromTheme('tabs', ...mode, 'container', 'borderRadius')({ theme })};
                box-shadow: ${fromTheme('tabs', ...mode, 'container', 'boxShadow')({ theme })};
            `
            : ''
    }
`;

TabWrapper = webOnlyStyles(TabWrapper)`
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

// Inner NavTab handles routing; minimal styling only
let StyledNavTab = styled(_NavTab).attrs({ name: 'tab-nav-tab-gl2025' })`
    text-decoration: none;
    color: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'text', 'color')({ theme })};
    font-size: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'text', 'fontSize')({ theme })};
    font-weight: ${({ mode, theme }) => {
        let value = theme.tabs;
        for (const key of mode) {
            value = value?.[key];
        }
        return value?.text?.fontWeight;
    }};
    line-height: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'text', 'lineHeight')({ theme })};
    font-family: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'text', 'fontFamily')({ theme })};
    opacity: ${({ mode, theme }) => {
        let value = theme.tabs;
        for (const key of mode) {
            value = value?.[key];
        }
        return value?.text?.opacity;
    }};
    white-space: nowrap;
`;

StyledNavTab = webOnlyStyles(StyledNavTab)`
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

/**
 * GL2025 styled route tab (navigation heading for TabNav).
 * Uses wrapper pattern: outer View for GL2025 styling, inner NavTab for routing.
 */
function TabNavHeadingGL2025(props) {
    const {
        currentRoute,
        hNode: { id, title },
        hNode
    } = props;

    const hiddenForViewport = useHiddenForViewPort(hNode);
    const { disabledByNetwork, hiddenByNetwork, disabledMessage } = useDisabledForNetwork(hNode);
    const isActive = useIsInActiveRoute(props);

    const [isHovered, handleMouseEnter, handleMouseLeave] = useTabHover();
    const [isFocused, handleFocus, handleBlur, handleMouseDown] = useTabFocus();

    if (hiddenForViewport || hiddenByNetwork) {
        return null;
    }

    const disabled = disabledByNetwork;

    // Mode computation (same pattern as Tab.gl2025.js)
    const mode = disabled
        ? ['disabled']
        : isActive
        ? isFocused
            ? ['current', 'focus']
            : isHovered
            ? ['current', 'hover']
            : ['current', 'default']
        : isFocused
        ? ['inactive', 'focus']
        : isHovered
        ? ['inactive', 'hover']
        : ['inactive', 'default'];

    // prettier-ignore
    return rc(TabWrapper, {
            mode,
            disabled,
            'aria-selected': isActive,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            onMouseDown: handleMouseDown,
            onFocus: handleFocus,
            onBlur: handleBlur,
            title: disabled ? disabledMessage : undefined
        },
        rc(StyledNavTab, {
            id,
            key: id,
            to: currentRoute,
            disabled,
            mode
        }, title)
    );
}

TabNavHeadingGL2025.propTypes = {
    currentRoute: PropTypes.string,
    children: PropTypes.array,
    hNode: PropTypes.object,
    hiddenForViewport: PropTypes.bool
};

export default TabNavHeadingGL2025;
